import { useEffect, useMemo, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
  MagnifyingGlass, DownloadSimple, PaperPlaneTilt, EnvelopeSimple, ArrowRight,
  Sparkle, CaretDown,
} from "@phosphor-icons/react";

const STAGE_ORDER = ["waiting", "qualified", "priority", "invited", "activated", "converted", "customer"];
const STAGE_LABEL = { waiting: "Waiting", qualified: "Qualified", priority: "Priority", invited: "Invited", activated: "Activated", converted: "Converted", customer: "Customer" };
const STAGE_TONE = {
  waiting: "bg-slate-100 text-slate-700",
  qualified: "bg-sky-100 text-sky-700",
  priority: "bg-gold-soft text-gold",
  invited: "bg-indigo-100 text-indigo-700",
  activated: "bg-teal-soft text-teal",
  converted: "bg-emerald-100 text-emerald-700",
  customer: "bg-green-100 text-green-700",
};
const PRIORITY_TONE = { urgent: "bg-red-100 text-red-700", high: "bg-amber-100 text-amber-700", medium: "bg-sky-100 text-sky-700", low: "bg-slate-100 text-slate-700" };

export default function AdminWaitlist() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [plan, setPlan] = useState("");
  const [investorType, setInvestorType] = useState("");
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openSeqFor, setOpenSeqFor] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/waitlist/config");
        setTypes(data.investor_types || []);
      } catch { /* noop */ }
    })();
  }, []);

  const reload = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/waitlist", {
        params: { q, stage, plan, investor_type: investorType, limit: 200 },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(extractApiError(e, "Could not load the waitlist."));
    }
    setBusy(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [stage, plan, investorType]);

  const setStageFor = async (leadId, next) => {
    try {
      await api.patch(`/admin/waitlist/${leadId}/stage`, { stage_v2: next });
      setItems((s) => s.map((it) => it.id === leadId ? { ...it, stage_v2: next } : it));
      toast.success(`Moved to ${STAGE_LABEL[next]}.`);
    } catch (e) { toast.error(extractApiError(e, "Could not update stage.")); }
  };

  const invite = async (leadId) => {
    try {
      const { data } = await api.post(`/admin/waitlist/${leadId}/invite`);
      toast.success(`Invitation sent · checkout ${data.checkout_url ? "prefilled" : "generated"}.`);
      setItems((s) => s.map((it) => it.id === leadId ? { ...it, stage_v2: "invited", invited_at: new Date().toISOString() } : it));
    } catch (e) { toast.error(extractApiError(e, "Could not send invitation.")); }
  };

  const sendSeq = async (leadId, sequence) => {
    try {
      await api.post(`/admin/waitlist/${leadId}/send-sequence`, { sequence });
      toast.success(`Sequence ${sequence} sent.`);
      setOpenSeqFor(null);
    } catch (e) { toast.error(extractApiError(e, "Could not send sequence.")); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("orixa_token");
    const backend = process.env.REACT_APP_BACKEND_URL;
    // Redirect via a temporary anchor with header — use fetch → blob
    fetch(`${backend}/api/admin/waitlist.csv${stage ? `?stage=${stage}` : ""}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "orixa-waitlist.csv"; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error("Export failed."));
  };

  const funnelSummary = useMemo(() => {
    const counts = {}; STAGE_ORDER.forEach((s) => counts[s] = 0);
    items.forEach((it) => counts[it.stage_v2] = (counts[it.stage_v2] || 0) + 1);
    return counts;
  }, [items]);

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="admin-waitlist-page">
      <PageHeader
        eyebrow="Early Access · Administration"
        title="Waitlist Management"
        subtitle="Review, prioritise, invite and convert every Early Access registration."
        action={
          <button onClick={exportCsv} className="inline-flex items-center gap-2 border border-border rounded-lg px-4 py-2 text-sm font-semibold hover:bg-secondary" data-testid="admin-wl-csv">
            <DownloadSimple size={14} weight="bold" /> Export CSV
          </button>
        }
      />

      {/* Funnel strip */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="admin-wl-funnel">
        {STAGE_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => setStage(stage === s ? "" : s)}
            className={`rounded-xl border p-3 text-left transition-all ${stage === s ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-border bg-card hover:bg-secondary"}`}
            data-testid={`admin-wl-funnel-${s}`}
          >
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{STAGE_LABEL[s]}</div>
            <div className="mt-0.5 data-large text-xl">{funnelSummary[s] || 0}</div>
          </button>
        ))}
      </div>

      {/* Filters row */}
      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-3 text-muted-foreground" />
          <input
            type="text" value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
            placeholder="Search by name, email, organisation…"
            className="w-full h-10 rounded-lg border border-border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="admin-wl-search"
          />
        </div>
        <select value={investorType} onChange={(e) => setInvestorType(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-3 text-sm" data-testid="admin-wl-type-filter">
          <option value="">All investor types</option>
          {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-3 text-sm" data-testid="admin-wl-plan-filter">
          <option value="">All plans</option>
          <option value="starter">Starter</option>
          <option value="professional">Professional</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <button onClick={reload} className="h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90" data-testid="admin-wl-search-btn">Search</button>
      </div>

      {/* Table */}
      <div className="mt-6 bg-card border border-border rounded-2xl overflow-hidden" data-testid="admin-wl-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60">
              <tr>
                <th className="text-left p-3 text-overline text-muted-foreground">Lead</th>
                <th className="text-left p-3 text-overline text-muted-foreground">Type · Plan</th>
                <th className="text-left p-3 text-overline text-muted-foreground">Country</th>
                <th className="text-right p-3 text-overline text-muted-foreground">Score</th>
                <th className="text-left p-3 text-overline text-muted-foreground">Priority</th>
                <th className="text-left p-3 text-overline text-muted-foreground">Stage</th>
                <th className="text-right p-3 text-overline text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">{busy ? "Loading…" : "No leads match these filters."}</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className="border-t border-border hover:bg-secondary/30" data-testid={`admin-wl-row-${it.id}`}>
                  <td className="p-3 whitespace-nowrap">
                    <div className="font-semibold">{it.full_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{it.email}</div>
                    <div className="text-[11px] text-muted-foreground/70">{it.organization}</div>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <div className="text-xs capitalize">{it.investor_type?.replace("_", " ") || "—"}</div>
                    <div className="text-[11px] text-muted-foreground capitalize">{it.preferred_plan || "—"}</div>
                  </td>
                  <td className="p-3 text-xs">{it.country || "—"}</td>
                  <td className="p-3 text-right"><span className="data-large text-base text-primary">{it.lead_score ?? "—"}</span></td>
                  <td className="p-3">
                    {it.priority ? <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${PRIORITY_TONE[it.priority] || "bg-secondary"}`}>{it.priority}</span> : "—"}
                  </td>
                  <td className="p-3">
                    <select
                      value={it.stage_v2}
                      onChange={(e) => setStageFor(it.id, e.target.value)}
                      className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 border-0 ${STAGE_TONE[it.stage_v2] || "bg-secondary"}`}
                      data-testid={`admin-wl-stage-${it.id}`}
                    >
                      {STAGE_ORDER.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right relative whitespace-nowrap">
                    {it.stage_v2 !== "invited" && it.stage_v2 !== "converted" && it.stage_v2 !== "customer" && (
                      <button onClick={() => invite(it.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline mr-3" data-testid={`admin-wl-invite-${it.id}`}>
                        <PaperPlaneTilt size={12} weight="bold" /> Invite
                      </button>
                    )}
                    <button
                      onClick={() => setOpenSeqFor(openSeqFor === it.id ? null : it.id)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                      data-testid={`admin-wl-seq-${it.id}`}
                    >
                      <EnvelopeSimple size={12} weight="bold" /> Sequence <CaretDown size={10} weight="bold" />
                    </button>
                    {openSeqFor === it.id && (
                      <div className="absolute right-4 top-10 z-10 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[200px]">
                        {[
                          { n: 2, label: "2 · Why Orixa" },
                          { n: 3, label: "3 · Intelligence examples" },
                          { n: 5, label: "5 · Billing is open" },
                          { n: 6, label: "6 · Complete subscription" },
                          { n: 7, label: "7 · Final reminder" },
                        ].map((s) => (
                          <button
                            key={s.n}
                            onClick={() => sendSeq(it.id, s.n)}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-secondary"
                            data-testid={`admin-wl-seq-${it.id}-${s.n}`}
                          >{s.label}</button>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-3 border-t border-border text-xs text-muted-foreground flex justify-between">
          <span>{items.length} / {total} leads</span>
          <span className="inline-flex items-center gap-1.5"><Sparkle size={12} weight="fill" className="text-gold" /> Sorted by lead score desc</span>
        </div>
      </div>
    </div>
  );
}
